// Subtle "ambient festival mode" effects that stay on while people work.
// Kept deliberately light: ~30 fps, a handful of particles, mostly at the edges,
// and static decorations (garland, ribbons) pre-drawn once per resize.
import { glowSprite, rand, pick, drawPetal } from './festivalEffects';

const FPS = 30;

// x position inside the left or right edge band, keeping the middle clear for work
const edgeX = (w) => {
  const band = Math.max(60, w * 0.1);
  return Math.random() < 0.5 ? rand(0, band) : rand(w - band, w);
};

// ---- decorations ---------------------------------------------------------------

// Marigold toran across the top: drooping bead strings with small leaf pendants.
function drawToran(ctx, w) {
  const span = 70;
  const sag = 8;
  const n = Math.ceil(w / span);
  for (let i = 0; i < n; i++) {
    const x0 = i * span;
    for (let s = 0; s <= span; s += 6) {
      const u = s / span;
      const y = 3 + Math.sin(u * Math.PI) * sag;
      ctx.fillStyle = (Math.floor(s / 6) + i) % 2 ? '#f97316' : '#facc15';
      ctx.beginPath();
      ctx.arc(x0 + s, y, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }
    // mango-leaf pendant at each joint
    ctx.fillStyle = '#15803d';
    ctx.beginPath();
    ctx.moveTo(x0 - 3.5, 4);
    ctx.quadraticCurveTo(x0, 20, x0 + 3.5, 4);
    ctx.fill();
  }
}

function drawDiya(ctx, x, y, s, flicker, flameColor = '#fbbf24') {
  // flame glow
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.5 + 0.3 * flicker;
  const g = s * (2.2 + 0.4 * flicker);
  ctx.drawImage(glowSprite(flameColor, 64), x - g, y - s * 1.3 - g, g * 2, g * 2);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  // flame
  ctx.fillStyle = '#fde68a';
  ctx.beginPath();
  ctx.moveTo(x, y - s * (1.6 + 0.3 * flicker));
  ctx.quadraticCurveTo(x + s * 0.45, y - s * 0.6, x, y - s * 0.35);
  ctx.quadraticCurveTo(x - s * 0.45, y - s * 0.6, x, y - s * (1.6 + 0.3 * flicker));
  ctx.fill();
  // clay bowl
  ctx.fillStyle = '#b45309';
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.2, s, s * 0.5, 0, 0, Math.PI);
  ctx.fill();
  ctx.fillStyle = '#d97706';
  ctx.fillRect(x - s, y - s * 0.3, s * 2, s * 0.18);
}

function drawRibbon(ctx, w) {
  ['#ff9933', '#ffffff', '#138808'].forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect(0, i * 2, w, 2);
  });
}

function drawKite(ctx, x, y, s, tilt, c1, c2) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  ctx.fillStyle = c1;
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.75, 0); ctx.lineTo(0, s * 1.1); ctx.closePath(); ctx.fill();
  ctx.fillStyle = c2;
  ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(-s * 0.75, 0); ctx.lineTo(0, s * 1.1); ctx.closePath(); ctx.fill();
  ctx.restore();
}

// ---- reusable particle pieces ------------------------------------------------------

// Mini firework bursts near the bottom corners every few seconds.
function cornerBursts() {
  const colors = ['#ffd166', '#ff6b6b', '#f72585', '#4cc9f0', '#80ffdb', '#ff9f1c'];
  const sparks = [];
  let timer = rand(1, 3);
  return (ctx, dt, w, h) => {
    timer -= dt;
    if (timer <= 0) {
      const x = edgeX(w);
      const y = rand(h * 0.6, h * 0.88);
      const color = pick(colors);
      for (let i = 0; i < 22; i++) {
        const a = (i / 22) * Math.PI * 2;
        const s = rand(40, 75);
        sparks.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, age: 0, life: rand(0.8, 1.2), color });
      }
      timer = rand(3.5, 7);
    }
    ctx.globalCompositeOperation = 'lighter';
    for (let i = sparks.length - 1; i >= 0; i--) {
      const p = sparks[i];
      p.age += dt;
      if (p.age >= p.life) { sparks.splice(i, 1); continue; }
      p.vy += 40 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      ctx.globalAlpha = 0.55 * (1 - p.age / p.life);
      ctx.drawImage(glowSprite(p.color, 32), p.x - 4, p.y - 4, 8, 8);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  };
}

// A few petals drifting down the side edges.
function edgePetals(w, h, palette, count = 12) {
  const make = (y) => ({ x: edgeX(w), y, vy: rand(18, 40), sway: rand(10, 25), phase: rand(0, 6), size: rand(4, 7), rot: rand(0, 3), vr: rand(-1, 1), flip: rand(0, 3), vf: rand(1, 3), color: pick(palette) });
  const list = Array.from({ length: count }, () => make(rand(-h, h)));
  return (ctx, dt, w, h) => {
    ctx.globalAlpha = 0.7;
    for (const p of list) {
      p.y += p.vy * dt;
      p.phase += dt;
      p.rot += p.vr * dt;
      p.flip += p.vf * dt;
      if (p.y > h + 10) Object.assign(p, make(-10));
      drawPetal(ctx, { ...p, x: p.x + Math.sin(p.phase) * p.sway });
    }
    ctx.globalAlpha = 1;
  };
}

// Twinkling sparkle stars spaced along the top border.
function topStars(color = '#fde68a', count = 10) {
  const stars = Array.from({ length: count }, (_, i) => ({ u: (i + rand(0.2, 0.8)) / count, y: rand(6, 26), phase: rand(0, 6), size: rand(5, 9) }));
  let t = 0;
  return (ctx, dt, w) => {
    t += dt;
    ctx.globalCompositeOperation = 'lighter';
    for (const s of stars) {
      ctx.globalAlpha = Math.max(0, Math.sin(t * 1.5 + s.phase)) * 0.8;
      ctx.drawImage(glowSprite(color, 32), s.u * w - s.size, s.y - s.size, s.size * 2, s.size * 2);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  };
}

// Pair of diyas in each bottom corner.
function cornerDiyas(flameColor) {
  let t = 0;
  return (ctx, dt, w, h) => {
    t += dt;
    const s = 9;
    // right pair sits left of the festive-vibe toggle button
    [[22, 0], [50, 1.3], [w - 92, 2.1], [w - 64, 3.4]].forEach(([x, ph]) => {
      drawDiya(ctx, x, h - 10, s, 0.5 + 0.5 * Math.sin(t * 7 + ph) * Math.sin(t * 3.1 + ph), flameColor);
    });
  };
}

// ---- ambient effects per festival --------------------------------------------------

const withStatic = (drawStatic, parts) => (w, h) => ({ drawStatic, parts: parts(w, h) });

export const AMBIENT = {
  diwali: withStatic(drawToran, () => [cornerDiyas(), cornerBursts()]),
  sparks: withStatic(drawToran, () => [cornerBursts()]),
  garland: withStatic(drawToran, () => []), // petals added in extraParts with the festival palette
  petals: withStatic(null, () => []),
  tricolor: withStatic(drawRibbon, (w, h) => [edgePetals(w, h, ['#ff9933', '#ffffff', '#138808'], 10)]),
  lights: withStatic(null, () => []),

  colors: withStatic(null, () => {
    const palette = [[236, 72, 153], [250, 204, 21], [34, 197, 94], [59, 130, 246], [168, 85, 247], [249, 115, 22]];
    let t = 0;
    return [(ctx, dt, w, h) => {
      t += dt;
      const r = Math.min(260, Math.max(w, h) * 0.22);
      [[0, 0], [w, 0], [0, h], [w, h]].forEach(([x, y], i) => {
        const k = (t * 0.15 + i * 1.5) % palette.length;
        const [r1, g1, b1] = palette[Math.floor(k)];
        const pulse = 0.18 + 0.06 * Math.sin(t * 0.8 + i);
        ctx.globalAlpha = pulse;
        ctx.drawImage(glowSprite(`rgb(${r1},${g1},${b1})`, 128), x - r, y - r, r * 2, r * 2);
      });
      ctx.globalAlpha = 1;
    }];
  }),

  snow: withStatic(null, (w, h) => {
    const flakes = Array.from({ length: 28 }, () => ({ x: edgeX(w), y: rand(-h, h), r: rand(1.5, 3.5), vy: rand(15, 35), phase: rand(0, 6), sway: rand(8, 20) }));
    return [topStars('#e0f2fe', 8), (ctx, dt, w, h) => {
      const sprite = glowSprite('rgba(255,255,255,0.95)', 32);
      ctx.globalAlpha = 0.75;
      for (const f of flakes) {
        f.y += f.vy * dt;
        f.phase += dt * 0.8;
        if (f.y > h + 5) { f.y = -5; f.x = edgeX(w); }
        const s = f.r * 2.6;
        ctx.drawImage(sprite, f.x + Math.sin(f.phase) * f.sway - s / 2, f.y - s / 2, s, s);
      }
      ctx.globalAlpha = 1;
    }];
  }),

  kites: withStatic(null, (w) => {
    const list = [
      { u: 0.04, y: 70, s: 14, c: ['#ef4444', '#facc15'], ph: 0 },
      { u: 0.93, y: 60, s: 16, c: ['#3b82f6', '#f97316'], ph: 2 },
      { u: 0.97, y: 120, s: 11, c: ['#22c55e', '#ec4899'], ph: 4 },
    ];
    let t = 0;
    return [(ctx, dt, w, h) => {
      t += dt;
      for (const k of list) {
        const x = k.u * w + Math.sin(t * 0.7 + k.ph) * 10;
        const y = k.y + Math.sin(t * 1.1 + k.ph) * 8;
        ctx.globalAlpha = 0.8;
        ctx.strokeStyle = 'rgba(100,116,139,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y + k.s * 1.1);
        ctx.quadraticCurveTo(x + (k.u < 0.5 ? 20 : -20), h * 0.5, k.u < 0.5 ? 0 : w, h * 0.75);
        ctx.stroke();
        drawKite(ctx, x, y, k.s, Math.sin(t * 1.4 + k.ph) * 0.2, k.c[0], k.c[1]);
      }
      ctx.globalAlpha = 1;
    }];
  }),

  confetti: withStatic(null, (w, h) => {
    const palette = ['#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#facc15'];
    const bits = [];
    let timer = 0;
    return [topStars('#fde68a', 12), (ctx, dt, w, h) => {
      timer -= dt;
      if (timer <= 0 && bits.length < 25) {
        bits.push({ x: edgeX(w), y: -10, vy: rand(25, 50), rot: rand(0, 3), vr: rand(-3, 3), flip: rand(0, 3), color: pick(palette) });
        timer = rand(0.3, 0.7);
      }
      ctx.globalAlpha = 0.75;
      for (let i = bits.length - 1; i >= 0; i--) {
        const b = bits[i];
        b.y += b.vy * dt;
        b.rot += b.vr * dt;
        b.flip += dt * 5;
        if (b.y > h + 10) { bits.splice(i, 1); continue; }
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.scale(1, Math.cos(b.flip));
        ctx.fillStyle = b.color;
        ctx.fillRect(-3, -4.5, 6, 9);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    }];
  }),
};

// Festival-palette parts for the generic types.
function extraParts(type, w, h, palette) {
  if (type === 'garland' || type === 'petals') return [edgePetals(w, h, palette || ['#f97316', '#facc15'], type === 'petals' ? 16 : 10)];
  if (type === 'lights') {
    const pal = palette || ['#fbbf24'];
    const motes = Array.from({ length: 14 }, () => ({ x: edgeX(w), y: rand(0, h), vy: -rand(10, 25), phase: rand(0, 6), size: rand(4, 9), color: pick(pal) }));
    let t = 0;
    return [cornerDiyas(pal[0]), (ctx, dt, w, h) => {
      t += dt;
      ctx.globalCompositeOperation = 'lighter';
      for (const m of motes) {
        m.y += m.vy * dt;
        if (m.y < -10) { m.y = h + 10; m.x = edgeX(w); }
        ctx.globalAlpha = 0.35 + 0.25 * Math.sin(t * 2 + m.phase);
        ctx.drawImage(glowSprite(m.color, 32), m.x + Math.sin(t * 0.6 + m.phase) * 8 - m.size, m.y - m.size, m.size * 2, m.size * 2);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }];
  }
  return [];
}

// Runs an ambient effect on a canvas; returns stop().
// With `still` (reduced motion) it draws only the static decoration once.
export function runAmbient(canvas, type, { palette, still = false } = {}) {
  const factory = AMBIENT[type];
  if (!factory || !canvas) return () => {};
  const ctx = canvas.getContext('2d');
  const staticLayer = document.createElement('canvas');
  let w = 0;
  let h = 0;
  let dpr = 1;
  let effect;

  const paintStatic = () => {
    staticLayer.width = canvas.width;
    staticLayer.height = canvas.height;
    const sctx = staticLayer.getContext('2d');
    sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    effect.drawStatic?.(sctx, w, h);
  };

  const resize = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (effect) paintStatic();
  };
  resize();
  const base = factory(w, h);
  effect = { drawStatic: base.drawStatic, parts: [...base.parts, ...extraParts(type, w, h, palette)] };
  paintStatic();

  const drawFrame = (dt) => {
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(staticLayer, 0, 0, w, h);
    for (const part of effect.parts) part(ctx, dt, w, h);
  };

  if (still) {
    const redraw = () => { resize(); drawFrame(0); };
    drawFrame(0);
    window.addEventListener('resize', redraw);
    return () => window.removeEventListener('resize', redraw);
  }

  window.addEventListener('resize', resize);
  let last = performance.now();
  let acc = 0;
  let raf = requestAnimationFrame(function frame(now) {
    acc += now - last;
    last = now;
    if (acc >= 1000 / FPS) {
      drawFrame(Math.min(acc / 1000, 0.1));
      acc = 0;
    }
    raf = requestAnimationFrame(frame);
  });

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}
